import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  CssBaseline,
  Divider,
  Grid,
  Paper,
  Stack,
  ThemeProvider,
  Typography,
  createTheme,
} from '@mui/material';

import {
  AccountBalanceWallet,
  ArrowForward,
  CheckCircle,
  Devices,
  Description,
  FlashOn,
  LockOutlined,
  MenuBook,
  People,
  School,
  TrendingUp,
} from '@mui/icons-material';

import Navbar from '../Navbar';
import Footer from '../HomeFooter';


// ======================================================
// THEME
// ======================================================

const theme = createTheme({
  palette: {
    primary: {
      main: '#4551be', // Changed to match previous code
    },
    secondary: {
      main: '#00c853',
    },
    background: {
      default: '#f0f4f8', // Changed to light blue-grey from previous code
    },
  },

  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',

    h1: {
      fontWeight: 800,
    },

    h2: {
      fontWeight: 700,
    },

    h3: {
      fontWeight: 700,
    },

    h4: {
      fontWeight: 600,
    },
  },

  shape: {
    borderRadius: 12,
  },
});


// ======================================================
// FEATURES
// ======================================================

const features = [
  {
    icon: <LockOutlined />,
    title: 'Simple OTP-Based Login',
    desc: 'Convenient user authentication.',
  },
  {
    icon: <Description />,
    title: 'Digital Access',
    desc: 'Provides users with a convenient platform to explore available financial services.',
  },
  {
    icon: <TrendingUp />,
    title: 'Easy User Experience',
    desc: 'Designed to simplify the digital journey for users.',
  },
  {
    icon: <MenuBook />,
    title: 'Financial Service Connectivity',
    desc: 'Facilitates connections between users and financial service providers.',
  },
  {
    icon: <Devices />,
    title: 'User-Friendly Interface',
    desc: 'A simple mobile experience designed for easy navigation.',
  },
];


// ======================================================
// FINANCIAL NEEDS
// ======================================================

const needs = [
  {
    icon: <AccountBalanceWallet />,
    label: 'Unexpected Expenses',
  },
  {
    icon: <School />,
    label: 'Education',
  },
  {
    icon: <People />,
    label: 'Personal Needs',
  },
  {
    icon: <TrendingUp />,
    label: 'Household Expenses',
  },
  {
    icon: <CheckCircle />,
    label: 'Other Financial Commitments',
  },
];


// ======================================================
// FEATURE CARD
// ======================================================

function FeatureCard({ icon, title, desc }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        border: '1px solid #e0e0e0',
        transition: 'all 0.25s ease',

        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
          borderColor: 'primary.main',
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: 'primary.main',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          {icon}
        </Box>

        <Typography
          variant="h6"
          gutterBottom
          fontWeight={600}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
        >
          {desc}
        </Typography>
      </CardContent>
    </Card>
  );
}


// ======================================================
// NEED CHIP
// ======================================================

function NeedChip({ icon, label }) {
  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2.5,
        py: 1.5,
        borderRadius: 50,
        border: '1px solid #e0e0e0',
        bgcolor: '#fff',
        transition: 'all 0.2s',

        '&:hover': {
          borderColor: 'secondary.main',
          bgcolor: '#f1f8e9',
        },
      }}
    >
      <Box
        sx={{
          color: 'secondary.main',
          display: 'flex',
        }}
      >
        {icon}
      </Box>

      <Typography
        variant="body2"
        fontWeight={500}
      >
        {label}
      </Typography>
    </Paper>
  );
}


// ======================================================
// Koottu
// ======================================================

export default function BowerCred() {
  return (
    <>
      {/* ==================================================
          NAVBAR
          IMPORTANT:
          Navbar is OUTSIDE ThemeProvider
      ================================================== */}

      <Navbar />


      {/* ==================================================
          Koottu THEME
      ================================================== */}

      <ThemeProvider theme={theme}>
        <CssBaseline />

        {/* ==================================================
            HERO
        ================================================== */}

        <Box
          sx={{
            background:
              'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)', // Changed to match previous code

            color: '#0f172a', // Changed to dark text

            py: {
              xs: 10,
              md: 14,
            },
          }}
        >
          <Container maxWidth="lg">

            <Grid
              container
              spacing={6}
              alignItems="center"
            >

              {/* LEFT CONTENT */}

              <Grid
                item
                xs={12}
                md={7}
              >
                <Typography
                  variant="overline"
                  sx={{
                    letterSpacing: 3,
                    opacity: 0.8,
                  }}
                >
                  Digital Credit Facilitation Platform
                </Typography>


                {/* LOGO + TITLE IN SAME ROW */}

                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ mt: 1, mb: 3 }}
                >

                  <Box
                    component="img"
                    src="/images/bower_logo.png"
                    alt="Koottu Logo"
                    sx={{
                      width: { xs: 48, md: 64 },
                      height: { xs: 48, md: 64 },
                      objectFit: 'contain',
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.1)',
                      p: 0.5,
                    }}
                  />


                  <Typography
                    variant="h2"
                    sx={{
                      lineHeight: 1.15,
                      fontSize: {
                        xs: '2.2rem',
                        md: '3.2rem',
                      },
                      color: '#4551be', // Added blue color
                    }}
                  >
                    Koottu
                  </Typography>

                </Stack>


                <Typography
                  variant="h6"
                  sx={{
                    opacity: 0.95,
                    mb: 2,
                    fontWeight: 400,
                    maxWidth: 620,
                    color: '#475569', // Dark grey text
                  }}
                >
                  Koottu is a mobile-based digital platform
                  designed to connect people seeking financial
                  assistance with financial service providers.
                </Typography>


                <Typography
                  variant="body1"
                  sx={{
                    opacity: 0.85,
                    mb: 4,
                    maxWidth: 620,
                    color: '#64748b', // Dark grey text
                  }}
                >
                  The application aims to simplify the process of
                  discovering and accessing credit-related services
                  by providing a convenient, user-friendly digital
                  interface.
                </Typography>

              </Grid>


              {/* RIGHT CARD */}

              <Grid
                item
                xs={12}
                md={5}
              >
                <Paper
                  elevation={12}
                  sx={{
                    p: 4,
                    borderRadius: 5,

                    background:
                      'linear-gradient(135deg, #3949ab 0%, #283593 60%, #1a237e 100%)',

                    color: '#fff',

                    border:
                      '1px solid rgba(255,255,255,0.15)',

                    boxShadow:
                      '0 12px 40px rgba(26, 35, 126, 0.45)',
                  }}
                >

                  <Typography
                    variant="h6"
                    gutterBottom
                    fontWeight={700}
                    sx={{
                      color: '#fff',
                    }}
                  >
                    Important Clarification
                  </Typography>


                  <Divider
                    sx={{
                      borderColor:
                        'rgba(255,255,255,0.25)',
                      mb: 2,
                    }}
                  />


                  <Stack spacing={2}>

                    {[
                      'Koottu does not provide, lend, or fund money.',
                      'Acts as a technology platform facilitating connections.',
                      'Financial products handled by respective providers.',
                      'Subject to provider eligibility criteria and policies.',
                    ].map((item) => (

                      <Stack
                        key={item}
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                      >

                        <CheckCircle
                          sx={{
                            color: '#00e676',
                            fontSize: 20,
                          }}
                        />

                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              'rgba(255,255,255,0.92)',
                          }}
                        >
                          {item}
                        </Typography>

                      </Stack>

                    ))}

                  </Stack>

                </Paper>
              </Grid>

            </Grid>

          </Container>
        </Box>


        {/* ==================================================
            PRODUCT OVERVIEW
        ================================================== */}

        <Container
          maxWidth="lg"
          sx={{
            py: {
              xs: 8,
              md: 10,
            },
          }}
        >

          <Grid
            container
            spacing={6}
            alignItems="center"
          >

            <Grid
              item
              xs={12}
              md={6}
            >

              <Typography
                variant="overline"
                color="secondary.main"
                fontWeight={700}
              >
                Product Overview
              </Typography>


              <Typography
                variant="h3"
                sx={{
                  mt: 1,
                  mb: 3,
                  color: '#1e293b', // Dark text
                }}
              >
                A digital platform connecting people with financial services
              </Typography>


              <Typography
                variant="body1"
                color="text.secondary"
                paragraph
                sx={{ color: '#64748b' }} // Dark grey text
              >
                Financial requirements can arise unexpectedly
                for education, emergencies, personal expenses,
                household needs, and other day-to-day
                requirements. Koottu provides a digital
                platform that helps users connect with relevant
                financial service providers.
              </Typography>


              <Typography
                variant="body1"
                color="text.secondary"
                paragraph
                sx={{ color: '#64748b' }} // Dark grey text
              >
                The platform is designed to make the overall
                journey more convenient through a simple
                digital experience.
              </Typography>

            </Grid>


            {/* PHONE MOCKUP */}

            <Grid
              item
              xs={12}
              md={6}
            >

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >

                <Paper
                  elevation={0}
                  sx={{
                    width: 260,
                    height: 520,
                    borderRadius: 6,
                    border: '8px solid #4551be', // Changed to blue
                    bgcolor: '#fff',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: 6,
                  }}
                >

                  <Box
                    sx={{
                      height: 28,
                      bgcolor: '#4551be', // Changed to blue
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: 60,
                        height: 6,
                        borderRadius: 3,
                        bgcolor:
                          'rgba(255,255,255,0.4)',
                      }}
                    />
                  </Box>


                  <Box sx={{ p: 2 }}>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Welcome back
                    </Typography>


                    <Typography
                      variant="h6"
                      fontWeight={700}
                      color="primary.main"
                      gutterBottom
                    >
                      Koottu
                    </Typography>


                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        borderRadius: 3,
                        bgcolor: '#e8eaf6',
                      }}
                    >

                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Explore Financial Services
                      </Typography>


                      <Typography
                        variant="h5"
                        fontWeight={700}
                        color="primary.main"
                      >
                        Connect Now
                      </Typography>

                    </Box>


                    <Stack
                      spacing={1.5}
                      sx={{ mt: 2 }}
                    >

                      {[
                        'Explore Services',
                        'My Applications',
                        'Track Status',
                        'Support',
                      ].map((item) => (

                        <Paper
                          key={item}
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: '#f5f5f5',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >

                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor:
                                'secondary.main',
                            }}
                          />

                          <Typography
                            variant="caption"
                            fontWeight={500}
                          >
                            {item}
                          </Typography>

                        </Paper>

                      ))}

                    </Stack>

                  </Box>

                </Paper>

              </Box>

            </Grid>

          </Grid>

        </Container>


        {/* ==================================================
            HOW Koottu WORKS
        ================================================== */}

        <Box
          id="how-it-works"
          sx={{
            bgcolor: '#fff',
            py: {
              xs: 8,
              md: 10,
            },
          }}
        >

          <Container maxWidth="lg">

            <Box
              sx={{
                textAlign: 'center',
                mb: 6,
              }}
            >

              <Typography
                variant="overline"
                color="secondary.main"
                fontWeight={700}
              >
                How Koottu Works
              </Typography>


              <Typography
                variant="h3"
                sx={{
                  mt: 1,
                  mb: 2,
                  color: '#1e293b', // Dark text
                }}
              >
                A digital bridge between users and financial service providers
              </Typography>


              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  maxWidth: 600,
                  mx: 'auto',
                  color: '#64748b', // Dark grey text
                }}
              >
                Koottu acts as a digital bridge between users
                and financial service providers.
              </Typography>

            </Box>


            {/* FLOW DIAGRAM */}

            <Grid
              container
              spacing={4}
              alignItems="center"
              justifyContent="center"
            >

              <Grid
                item
                xs={12}
                md={3}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    textAlign: 'center',
                    border: '2px solid #e0e0e0',
                    bgcolor: '#f5f7fb',
                  }}
                >

                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <People sx={{ fontSize: 32 }} />
                  </Box>


                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{ color: '#1e293b' }} // Dark text
                  >
                    People Seeking Financial Assistance
                  </Typography>

                </Paper>
              </Grid>


              <Grid
                item
                xs={12}
                md={1}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <ArrowForward
                  sx={{
                    fontSize: 40,
                    color: 'secondary.main',
                    transform: {
                      xs: 'rotate(90deg)',
                      md: 'none',
                    },
                  }}
                />
              </Grid>


              <Grid
                item
                xs={12}
                md={3}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    textAlign: 'center',
                    border: '2px solid #4551be', // Changed to blue
                    bgcolor: '#e8eaf6',
                  }}
                >

                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: 'secondary.main',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <Devices sx={{ fontSize: 32 }} />
                  </Box>


                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color="primary.main"
                  >
                    Koottu Platform
                  </Typography>

                </Paper>
              </Grid>


              <Grid
                item
                xs={12}
                md={1}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <ArrowForward
                  sx={{
                    fontSize: 40,
                    color: 'secondary.main',
                    transform: {
                      xs: 'rotate(90deg)',
                      md: 'none',
                    },
                  }}
                />
              </Grid>


              <Grid
                item
                xs={12}
                md={3}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    textAlign: 'center',
                    border: '2px solid #e0e0e0',
                    bgcolor: '#f5f7fb',
                  }}
                >

                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <AccountBalanceWallet sx={{ fontSize: 32 }} />
                  </Box>


                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{ color: '#1e293b' }} // Dark text
                  >
                    Financial Service Providers
                  </Typography>

                </Paper>
              </Grid>

            </Grid>


            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                textAlign: 'center',
                mt: 4,
                maxWidth: 700,
                mx: 'auto',
                color: '#64748b', // Dark grey text
              }}
            >
              The platform facilitates the connection and digital
              journey between the user and the respective
              financial service provider.
            </Typography>

          </Container>

        </Box>


        {/* ==================================================
            KEY FEATURES
        ================================================== */}

        <Box
          id="features"
          sx={{
            py: {
              xs: 8,
              md: 10,
            },
          }}
        >

          <Container maxWidth="lg">

            <Box
              sx={{
                textAlign: 'center',
                mb: 6,
              }}
            >

              <Typography
                variant="overline"
                color="secondary.main"
                fontWeight={700}
              >
                Key Features
              </Typography>


              <Typography
                variant="h3"
                sx={{
                  mt: 1,
                  mb: 2,
                  color: '#1e293b', // Dark text
                }}
              >
                Everything you need in one app
              </Typography>


              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  maxWidth: 600,
                  mx: 'auto',
                  color: '#64748b', // Dark grey text
                }}
              >
                A simple, accessible, and user-friendly
                digital experience designed for individuals
                seeking financial assistance.
              </Typography>

            </Box>


            <Grid
              container
              spacing={3}
            >

              {features.map((f) => (

                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={f.title}
                >
                  <FeatureCard {...f} />
                </Grid>

              ))}

            </Grid>

          </Container>

        </Box>


        {/* ==================================================
            SUPPORTING FINANCIAL NEEDS
        ================================================== */}

        <Box
          id="needs"
          sx={{
            bgcolor: '#fff',
            py: {
              xs: 8,
              md: 10,
            },
          }}
        >

          <Container maxWidth="lg">

            <Grid
              container
              spacing={6}
              alignItems="center"
            >

              <Grid
                item
                xs={12}
                md={5}
              >

                <Typography
                  variant="overline"
                  color="secondary.main"
                  fontWeight={700}
                >
                  Supporting Financial Needs
                </Typography>


                <Typography
                  variant="h3"
                  sx={{
                    mt: 1,
                    mb: 3,
                    color: '#1e293b', // Dark text
                  }}
                >
                  Designed for every background and need
                </Typography>


                <Typography
                  variant="body1"
                  color="text.secondary"
                  paragraph
                  sx={{ color: '#64748b' }} // Dark grey text
                >
                  Koottu is designed for people from
                  different backgrounds and financial needs,
                  providing a convenient digital platform to
                  access credit-related services.
                </Typography>


                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ color: '#64748b' }} // Dark grey text
                >
                  Whether the requirement is related to an
                  unexpected expense, education, personal
                  needs, household expenses, or other financial
                  commitments, the platform aims to make the
                  process more accessible through digital
                  technology.
                </Typography>

              </Grid>


              <Grid
                item
                xs={12}
                md={7}
              >

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                  }}
                >

                  {needs.map((n) => (
                    <NeedChip
                      key={n.label}
                      {...n}
                    />
                  ))}

                </Box>

              </Grid>

            </Grid>

          </Container>

        </Box>


        {/* ==================================================
            OUR APPROACH
        ================================================== */}

        <Box
          id="approach"
          sx={{
            py: {
              xs: 8,
              md: 10,
            },
          }}
        >

          <Container maxWidth="lg">

            <Grid
              container
              spacing={6}
              alignItems="center"
            >

              <Grid
                item
                xs={12}
                md={6}
              >

                <Paper
                  elevation={0}
                  sx={{
                    p: 5,
                    borderRadius: 4,

                    background:
                      'linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)',
                  }}
                >

                  <Typography
                    variant="h4"
                    fontWeight={700}
                    color="primary.main"
                    gutterBottom
                  >
                    Lider Technology Solutions
                  </Typography>


                  <Typography
                    variant="body1"
                    color="text.secondary"
                    paragraph
                    sx={{ color: '#64748b' }} // Dark grey text
                  >
                    At Lider Technology Solutions, we develop
                    technology platforms that help simplify
                    real-world processes.
                  </Typography>


                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ color: '#64748b' }} // Dark grey text
                  >
                    With Koottu, our objective is to provide
                    a digital technology platform that facilitates
                    connectivity between people and financial
                    service providers, without directly providing
                    or funding financial services.
                  </Typography>

                </Paper>

              </Grid>


              <Grid
                item
                xs={12}
                md={6}
              >

                <Typography
                  variant="overline"
                  color="secondary.main"
                  fontWeight={700}
                >
                  Our Approach
                </Typography>


                <Typography
                  variant="h3"
                  sx={{
                    mt: 1,
                    mb: 3,
                    color: '#1e293b', // Dark text
                  }}
                >
                  Technology that connects people with financial services
                </Typography>


                <Stack spacing={2.5}>

                  {[
                    {
                      title: 'Simple',
                      desc: 'An intuitive interface that anyone can use.',
                    },
                    {
                      title: 'Accessible',
                      desc: 'Designed to reach people across different backgrounds.',
                    },
                    {
                      title: 'Digitally Driven',
                      desc: 'Leveraging technology to simplify the credit journey.',
                    },
                  ].map((item) => (

                    <Stack
                      key={item.title}
                      direction="row"
                      spacing={2}
                      alignItems="flex-start"
                    >

                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          bgcolor: 'secondary.main',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          fontWeight: 700,
                        }}
                      >
                        ✓
                      </Box>


                      <Box>

                        <Typography
                          variant="h6"
                          fontWeight={600}
                          sx={{ color: '#1e293b' }} // Dark text
                        >
                          {item.title}
                        </Typography>


                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ color: '#64748b' }} // Dark grey text
                        >
                          {item.desc}
                        </Typography>

                      </Box>

                    </Stack>

                  ))}

                </Stack>

              </Grid>

            </Grid>

          </Container>

        </Box>


        {/* ==================================================
            IMPORTANT CLARIFICATION
        ================================================== */}

        <Box
          id="clarification"
          sx={{
            bgcolor: '#fff',
            py: {
              xs: 8,
              md: 10,
            },
          }}
        >

          <Container maxWidth="md">

            <Paper
              elevation={0}
              sx={{
                p: 5,
                borderRadius: 4,
                border: '2px solid #ff9800',
                bgcolor: '#fff8e1',
              }}
            >

              <Typography
                variant="overline"
                color="#e65100"
                fontWeight={700}
              >
                Important Clarification
              </Typography>


              <Typography
                variant="h5"
                fontWeight={700}
                sx={{
                  mt: 1,
                  mb: 3,
                  color: '#e65100',
                }}
              >
                Koottu does not provide, lend, or fund money to users.
              </Typography>


              <Typography
                variant="body1"
                color="text.secondary"
                paragraph
                sx={{ color: '#64748b' }} // Dark grey text
              >
                The company does not directly conduct lending or
                money-financing activities through the application.
                Koottu functions as a technology platform that
                facilitates the connection between people seeking
                financial services and independent financial service
                providers.
              </Typography>


              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ color: '#64748b' }} // Dark grey text
              >
                Any financial product, credit facility, loan,
                approval, terms, conditions, and disbursement are
                handled by the respective financial service provider,
                subject to their applicable eligibility criteria and
                policies.
              </Typography>

            </Paper>

          </Container>

        </Box>


        {/* ==================================================
            VISION
        ================================================== */}

        <Box
          id="vision"
          sx={{
            background:
              'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)', // Changed to light

            color: '#0f172a', // Changed to dark text

            py: {
              xs: 8,
              md: 10,
            },
          }}
        >

          <Container
            maxWidth="md"
            sx={{
              textAlign: 'center',
            }}
          >

            <Typography
              variant="overline"
              sx={{
                letterSpacing: 3,
                opacity: 0.8,
                color: '#4551be', // Blue text
              }}
            >
              Vision
            </Typography>


            <Typography
              variant="h3"
              sx={{
                mt: 2,
                mb: 3,
                color: '#1e293b', // Dark text
              }}
            >
              Making access to financial service providers simpler
            </Typography>


            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                fontWeight: 400,
                color: '#64748b', // Dark grey text
              }}
            >
              Koottu aims to make access to financial service
              providers simpler, more convenient, and digitally
              accessible, while providing a seamless technology
              experience for users.
            </Typography>


            <Typography
              variant="h6"
              sx={{
                mt: 4,
                fontWeight: 700,
                color: '#4551be', // Blue text
              }}
            >
              Koottu — Connecting People with Financial Services.
            </Typography>

          </Container>

        </Box>


        {/* ==================================================
            FOOTER
        ================================================== */}

        <Footer />

      </ThemeProvider>
    </>
  );
}